import { useLiveQuery } from 'dexie-react-hooks';
import { Archive, FolderOpen, History, LockKeyhole } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Card, Dato, Emblema, Seccion } from '@/components/Card';
import { Fila, Filas } from '@/components/Fila';
import { leerSettings, totalRegistros } from '@/db/schema';
import { nombrePerfil } from '@/db/backup-format';
import { listarCopias } from '@/db/copias';
import { espacioDeFotos } from '@/db/fotos';
import { describirUltimoRespaldo, textoUltimoRespaldo } from '@/domain/backup-reminder';
import { formatearBytes } from '@/lib/storage';
import { HacerRespaldo } from './HacerRespaldo';
import { RUTA_RESPALDOS } from './BackupBanner';

export const VOLVER_A_RESPALDOS = {
  a: RUTA_RESPALDOS,
  texto: 'Respaldos',
  etiqueta: 'Volver a Respaldos',
} as const;

/**
 * Más → Respaldos (Req. 6, 7.3), con el estilo de Foco: el respaldo arriba y,
 * en pantallas aparte, revisar y restaurar, el cifrado y las copias internas.
 * La prioridad de la Fase 0: sin nube, este archivo es la única forma de no
 * perder los datos.
 */
export function BackupPage() {
  const settings = useLiveQuery(() => leerSettings(), []);
  const registros = useLiveQuery(() => totalRegistros(), [], 0);
  const copias = useLiveQuery(() => listarCopias(), []);
  const fotos = useLiveQuery(() => espacioDeFotos(), []);

  if (settings === undefined) return null;

  const ultimo = settings.ultimoRespaldoEn;
  const cifrado = settings.cifradoRespaldo !== null;

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Tus datos, a salvo."
        descripcion="Tus datos viven solo en este teléfono. El respaldo es la única forma de recuperarlos."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      <Card>
        <Emblema icono={Archive} />
        <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">
          {ultimo === null ? 'Tu primera copia' : 'Hacer un respaldo'}
        </h2>
        <p className="text-[13px] leading-relaxed text-texto-suave">
          Un archivo con tu perfil y todos tus registros. Es lo único que permite recuperarlos.
        </p>
        <dl className="mt-3 divide-y divide-borde">
          <Dato
            etiqueta="Último respaldo"
            valor={ultimo === null ? 'Nunca' : textoUltimoRespaldo(ultimo)}
            {...(ultimo !== null ? { detalle: describirUltimoRespaldo(ultimo) } : {})}
          />
          <Dato
            etiqueta="Perfil de este teléfono"
            valor={nombrePerfil({ id: settings.perfilId, nombre: settings.nombre })}
          />
          <Dato etiqueta="Registros guardados" valor={String(registros)} />
          {fotos !== undefined && fotos.fotos > 0 && (
            <Dato
              etiqueta="Fotos de progreso"
              valor={`${fotos.fotos === 1 ? '1 foto' : `${fotos.fotos} fotos`} · ${formatearBytes(fotos.bytes)}`}
              detalle="Van en el respaldo con fotos, no en el de siempre"
            />
          )}
          <Dato etiqueta="Protección" valor={cifrado ? 'Con contraseña' : 'Sin contraseña'} />
        </dl>
        {/* La llave reinicia el formulario si una restauración cambia el perfil o si se
            activa o desactiva el cifrado: un respaldo preparado antes no se entrega después */}
        <HacerRespaldo
          key={`hacer-${settings.perfilId}-${settings.cifradoRespaldo?.activadoEn ?? 'sin-cifrado'}`}
          settings={settings}
        />
      </Card>

      <Filas>
        <Fila
          icono={FolderOpen}
          titulo="Revisar y restaurar"
          detalle="Elige un archivo y revisa su contenido antes de restaurar"
          a={`${RUTA_RESPALDOS}/restaurar`}
        />
        <Fila
          icono={LockKeyhole}
          titulo="Protección con contraseña"
          detalle={cifrado ? 'Cifrado activado' : 'Cifrado opcional, desactivado'}
          a={`${RUTA_RESPALDOS}/cifrado`}
        />
        <Fila
          icono={History}
          titulo="Copias internas"
          detalle={
            copias === undefined || copias.length === 0
              ? 'Protección antes de restaurar'
              : copias.length === 1
                ? '1 copia guardada'
                : `${copias.length} copias guardadas`
          }
          a={`${RUTA_RESPALDOS}/copias`}
        />
      </Filas>

      <Seccion titulo="Antes de guardar un respaldo">
        <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-texto-suave">
          <li>
            El archivo contiene tus datos personales: pesos, medidas, comidas y ayunos. Guárdalo en
            un lugar privado, como iCloud Drive, y no lo mandes por correo ni por chats.
          </li>
          <li>Copiar como texto deja el contenido en el portapapeles del iPhone.</li>
          <li>
            Tus fotos de progreso solo van en el «Respaldo con fotos», que es un archivo .zip. El
            respaldo de siempre, en .json, no las lleva; restaurarlo tampoco las borra.
          </li>
          <li>
            El respaldo de iCloud del iPhone no sustituye este respaldo: no hay garantía de que
            incluya los datos de esta app.
          </li>
        </ul>
      </Seccion>

      <Banner tono="alerta" titulo="No borres el ícono ni los datos de Safari sin un respaldo">
        En el iPhone, eliminar la app de la pantalla de inicio o borrar el historial y los datos de
        sitios elimina toda la información de DuoFit, y no se puede recuperar sin un archivo de
        respaldo.
      </Banner>
    </div>
  );
}
